/**
 * Smooth scroll interpolation hook.
 * Instead of jumping instantly, scroll position eases toward target.
 */

import { useRef, useCallback } from 'react'

const SCROLL_LERP = 0.15 // interpolation factor (0-1, higher = snappier)

export function useSmoothedScroll(
  getScrollY: () => number,
  setScrollY: (y: number) => void,
  getMaxScroll: () => number,
) {
  const targetRef = useRef(0)
  const currentRef = useRef(0)

  const scrollBy = useCallback((delta: number) => {
    const maxScroll = getMaxScroll()
    targetRef.current = Math.max(0, Math.min(maxScroll, targetRef.current + delta))
  }, [getMaxScroll])

  const scrollTo = useCallback((y: number) => {
    const maxScroll = getMaxScroll()
    targetRef.current = Math.max(0, Math.min(maxScroll, y))
  }, [getMaxScroll])

  // Call this every frame in the render loop
  const tick = useCallback(() => {
    const diff = targetRef.current - currentRef.current
    if (Math.abs(diff) < 0.5) {
      if (currentRef.current !== targetRef.current) {
        currentRef.current = targetRef.current
        setScrollY(currentRef.current)
      }
      return
    }
    currentRef.current += diff * SCROLL_LERP
    setScrollY(currentRef.current)
  }, [setScrollY])

  // Sync target with external scroll changes
  const sync = useCallback(() => {
    const current = getScrollY()
    currentRef.current = current
    targetRef.current = current
  }, [getScrollY])

  return { scrollBy, scrollTo, tick, sync }
}
